/**
 * Integrations Registry - the full integration definitions list
 */

import type { IntegrationDefinition } from './types.js';

export const INTEGRATIONS: IntegrationDefinition[] = [
  // ═══════════════════════════════════════
  // MESSAGING
  // ═══════════════════════════════════════
  // Messenger / Instagram DM / WhatsApp tiles were replaced by the unified
  // MetaCard rendered above the grid (one Meta account per workspace — one
  // OAuth connects the Page + its Instagram together; WhatsApp connects from
  // the same card). Do not re-add per-platform tiles: their independent
  // OAuths are exactly how workspaces ended up on two different Meta accounts.
  {
    id: 'telegram',
    name: 'Telegram Community',
    description: 'Connect your bot: tracked invites, join/leave automation triggers, community churn signals',
    category: 'messaging',
    icon: 'Send',
    authType: 'api_key',
    // Coming soon: not live for a new customer today (config/featureAvailability.ts).
    status: 'coming_soon',
    fields: [
      { key: 'botToken', label: 'Bot API token (from @BotFather)', placeholder: '123456:ABC-DEF…', type: 'password' },
    ],
    color: '#0088CC',
  },
  {
    id: 'community_link',
    name: 'Community Link (WhatsApp / Skool)',
    description: 'Paste your invite link — flows distribute it with click tracking. Join/leave tracking is Telegram-only.',
    category: 'messaging',
    icon: 'Users',
    authType: 'api_key',
    // Coming soon: not live for a new customer today (config/featureAvailability.ts).
    status: 'coming_soon',
    fields: [
      { key: 'inviteUrl', label: 'Community invite link', placeholder: 'https://chat.whatsapp.com/…', type: 'text' },
    ],
    color: '#22C55E',
  },
    {
    id: 'claude',
    name: 'Claude',
    description: 'Connect Claude AI (Code or Desktop) to manage marketing via the Model Context Protocol',
    category: 'ai',
    alsoIn: ['operations'],
    icon: 'Bot',
    // MCP clients sign in through the browser (OAuth); a bz_live_ key is only
    // for scripts and the CLI, so the card must not say "API key".
    authType: 'oauth',
    status: 'available',
    color: '#18181B',
  },

  // ═══════════════════════════════════════
  // E-COMMERCE (6)
  // ═══════════════════════════════════════
  {
    id: 'shopify',
    name: 'Shopify',
    description: 'Sync products, orders, and customers from your store',
    category: 'ecommerce',
    icon: 'ShoppingBag',
    authType: 'oauth',
    // Connect is live (shopify.connect). Catalog/order sync claims stay
    // Coming soon — see config/featureAvailability.ts.
    status: 'available',
    connectUrl: '/api/integrations/shopify/install',
    color: '#96BF48',
  },
  {
    id: 'woocommerce',
    name: 'WooCommerce',
    description: 'Connect your WordPress store for seamless sync',
    category: 'ecommerce',
    icon: 'Store',
    authType: 'api_key',
    // Coming soon: not live for a new customer today (config/featureAvailability.ts).
    status: 'coming_soon',
    fields: [
      { key: 'storeUrl', label: 'Store URL', placeholder: 'https://mystore.com', type: 'text' },
      { key: 'consumerKey', label: 'Consumer Key', placeholder: 'ck_xxxxx', type: 'password' },
      { key: 'consumerSecret', label: 'Consumer Secret', placeholder: 'cs_xxxxx', type: 'password' },
    ],
    color: '#96588A',
  },
    {
    id: 'wix',
    name: 'Wix',
    description: 'Connect your Wix e-commerce store for product, order, and customer sync',
    category: 'ecommerce',
    icon: 'Globe',
    authType: 'oauth',
    // Coming soon: not live for a new customer today (config/featureAvailability.ts).
    status: 'coming_soon',
    connectUrl: '/api/integrations/wix/install',
    color: '#0C6EFC',
  },
        
  // ═══════════════════════════════════════
  // EMAIL (6)
  // ═══════════════════════════════════════
  {
    id: 'email_sending',
    name: 'Email Sending',
    description: 'Configure your store email domain for white-label sending',
    category: 'email',
    icon: 'Mail',
    authType: 'api_key',
    // Coming soon: not live for a new customer today (config/featureAvailability.ts).
    status: 'coming_soon',
    fields: [
      { key: 'fromEmail', label: 'From Email', placeholder: 'noreply@yourdomain.com', type: 'text' },
      { key: 'fromName', label: 'From Name', placeholder: 'Your Store', type: 'text' },
    ],
    color: '#18181B',
  },
    {
    id: 'gmail',
    name: 'Gmail AI Support',
    description: 'Auto-reply to customer emails using AI + Shopify data',
    category: 'email',
    icon: 'Mail',
    authType: 'oauth',
    connectUrl: '/api/integrations/gmail/install',
    // Connect is live (gmail.connect). AI auto-reply stays Coming soon
    // (gmail.aiSupport) — see config/featureAvailability.ts.
    status: 'available',
    color: '#EA4335',
  },
  
  // ═══════════════════════════════════════
  // MARKETING (4)
  // ═══════════════════════════════════════
  // Meta Ads keeps its entry (the category pills, PLATFORM_TO_REGISTRY_IDS, the
  // setup checklist and the funnel definitions all key off this id) but has NO
  // connectUrl: the row on the Integrations page is rendered by MetaAdsCard,
  // which connects through /api/ads — the module that actually requests ads
  // scopes and discovers ad accounts.
  //
  // The URL that was here, `/api/integrations/meta/install?platform=facebook&
  // scope=ads`, was the MESSAGING OAuth. `scope` is not in that endpoint's
  // query schema, so Zod dropped it: the merchant consented to Messenger
  // permissions, no ads scope was ever requested, and the Ads tab went on
  // failing with SCOPE_MISSING. Do not restore it.
  {
    id: 'meta_ads',
    name: 'Meta Ads',
    description: 'Facebook and Instagram advertising platform',
    category: 'marketing',
    icon: 'Megaphone',
    authType: 'oauth',
    status: 'available',
    alsoIn: ['social'],
    color: '#1877F2',
  },
  {
    id: 'google_ads',
    name: 'Google Ads',
    description: 'Search, display, and YouTube advertising',
    category: 'marketing',
    icon: 'Target',
    authType: 'oauth',
    // Connect is live (googleAds.connect). Campaign management stays Coming soon
    // (ads.adAccounts) — see config/featureAvailability.ts.
    status: 'available',
    connectUrl: '/api/integrations/google-ads/install',
    color: '#4285F4',
  },
  {
    id: 'linkedin_marketing',
    name: 'LinkedIn',
    // Member posting only (w_member_social). Company pages need w_organization_social,
    // a Marketing Developer Platform product LinkedIn grants separately.
    description: 'Publish posts, photos and videos to your LinkedIn profile',
    category: 'social',
    icon: 'Linkedin',
    authType: 'oauth',
    status: 'available',
    connectUrl: '/api/social/oauth/linkedin/install',
    color: '#0A66C2',
  },
  {
    id: 'youtube',
    name: 'YouTube',
    description: 'Upload videos to your channel from the content calendar',
    category: 'social',
    icon: 'Youtube',
    authType: 'oauth',
    // Connect is live (social.youtubeConnect). Publishing stays Coming soon
    // until a live QA PASS — see config/featureAvailability.ts.
    status: 'available',
    connectUrl: '/api/social/oauth/youtube/install',
    color: '#FF0000',
  },
  {
    id: 'x_social',
    name: 'X',
    description: 'Publish posts and threads to your X account',
    category: 'social',
    icon: 'Hash',
    authType: 'oauth',
    // Connect is live (social.xConnect). Publishing stays Coming soon
    // until a live QA PASS — see config/featureAvailability.ts.
    status: 'available',
    connectUrl: '/api/social/oauth/x/install',
    color: '#000000',
  },
  {
    id: 'tiktok_business',
    name: 'TikTok for Creator',
    description: 'Connect your TikTok creator account (Login Kit). Video and photo posting needs Content Posting API approval.',
    category: 'social',
    icon: 'Clapperboard',
    authType: 'oauth',
    status: 'available',
    connectUrl: '/api/integrations/tiktok/install',
    color: '#EE1D52',
  },
  {
    id: 'tiktok_ads',
    name: 'TikTok for Business',
    description: 'Connect TikTok Marketing API and list your advertiser accounts',
    category: 'social',
    alsoIn: ['marketing'],
    icon: 'Clapperboard',
    authType: 'oauth',
    // Connect is live (ads.tiktokBusiness). Campaign UI stays Coming soon.
    status: 'available',
    connectUrl: '/api/integrations/tiktok-ads/install',
    color: '#EE1D52',
  },
        
  // ═══════════════════════════════════════
  // AI & ASSISTANTS
  // ═══════════════════════════════════════
  // Four doors onto the ONE hosted MCP endpoint. None of these stores a
  // credential on BuzzBit X: the merchant's own AI connects to the endpoint
  // and signs in from its side with browser OAuth, and every bulk send still
  // parks for approval in the app. `authType: 'none'` because the
  // page has nothing to ask for — the setup modal shows where to paste what.
  // Connected state is deliberately not tracked here: a host that has signed
  // in appears under Settings → Connected apps, which is the truthful list.
  {
    id: 'claude_code',
    name: 'Claude Code',
    description: 'Connect Claude Code with one command and sign in in the browser: read your numbers, draft flows and campaigns from the terminal',
    category: 'ai',
    icon: 'Terminal',
    authType: 'none',
    status: 'available',
    docsUrl: '/docs/mcp',
    color: '#D97757',
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    description: 'Add BuzzBit X as a connector in ChatGPT — sign in with your browser, no key to paste',
    category: 'ai',
    icon: 'MessageSquare',
    authType: 'none',
    // Coming soon: not live for a new customer today (config/featureAvailability.ts).
    status: 'coming_soon',
    docsUrl: '/docs/mcp',
    color: '#10A37F',
  },
  {
    id: 'codex',
    name: 'Codex CLI',
    description: 'Connect OpenAI Codex with one command (codex mcp add) and sign in in the browser',
    category: 'ai',
    icon: 'SquareTerminal',
    authType: 'none',
    status: 'available',
    docsUrl: '/docs/mcp',
    color: '#000000',
  },
  {
    id: 'local_llm',
    name: 'Local LLM (Ollama, LM Studio)',
    description: 'Run a model on your own machine and use it from Claude Code or Codex. LM Studio, Jan and Open WebUI: coming soon',
    category: 'ai',
    icon: 'Cpu',
    authType: 'none',
    // Coming soon: not live for a new customer today (config/featureAvailability.ts).
    status: 'coming_soon',
    docsUrl: '/docs/mcp',
    color: '#6366F1',
  },

  // ═══════════════════════════════════════
  // ACCOUNTING (4)
  // ═══════════════════════════════════════
  // Cardcom deliberately has NO marketplace card: it is the PLATFORM's own
  // billing rail (subscription charges via the Cardcom billing service), not a
  // merchant-connectable integration. Exposing it here invited merchants to
  // paste terminal credentials into a card that nothing on the merchant side
  // consumes. The billing service itself is untouched.
];
